
import { point as toPoint } from "@turf/turf";
import Line from "components/vector_map/widgets/line";
import AnimatableLine from "./animatable_line";
import PointMarker from "./point_marker";
import MidwayMarker from "./midway_marker";

function buildRouteLayerEntities({
  featureCollection,
  style,
  map,
  animationTime,
  detailsCallback = null
}) {
  const entities = {};

  if (style.outline && style.outline.opacity != 0) {
    entities.outline = new Line({ featureCollection, map, lineStyle: style.outline });
  }

  entities.line = new AnimatableLine({
    featureCollection,
    map,
    lineStyle: style.line,
    detailsOnHover: detailsCallback,
    time: animationTime
  });
  entities.startMarker = new PointMarker({
    map,
    style: style.start_marker,
    point: toPoint(entities.line.startCoordinates())
  });
  entities.endMarker = new PointMarker({
    map,
    style: style.finish_marker,
    point: toPoint(entities.line.finishCoordinates())
  });
  entities.animationMarker = new PointMarker({ map, style: style.animation_marker });
  entities.midwayMarker = new MidwayMarker({
    startMarker: entities.startMarker,
    endMarker: entities.endMarker,
    map
  });

  return entities;
}

class RouteLayerManager {
  _entityKeys = ['outline', 'line', 'startMarker', 'endMarker', 'midwayMarker', 'animationMarker'];

  constructor({ outline = null, line, startMarker, endMarker, animationMarker, midwayMarker }) {
    this.outline = outline;
    this.line = line,
    this.startMarker = startMarker;
    this.endMarker = endMarker;
    this.animationMarker = animationMarker;
    this.midwayMarker = midwayMarker;

    // Hook to determine whether to show start/end markers
    // or the midway marker.
    //
    this.zoomEndFunction = () => {
      if (this.isAnimating() || this.interrupt) return;

      this.showStartAndEndMarkers();
    };
  }

  areStartAndEndMarkersConflated() {
    const startPixelPoint = this.startMarker.pixelPoint();
    const midPixelPoint = this.midwayMarker.pixelPoint();
    const distance = startPixelPoint.dist(midPixelPoint);

    return distance < this.midwayMarker.styleOptions.size / 2;
  }

  show() {
    this.outline && this.outline.show();
    this.line.show();
    this.showStartAndEndMarkers();
    this.midwayMarker.map.on('zoomend', this.zoomEndFunction);
  }

  hide() {
    this.outline && this.outline.hide();
    this.line.hide();
    this.startMarker.hide();
    this.endMarker.hide();
    this.midwayMarker.hide();
    this.midwayMarker.map.off('zoomend', this.zoomEndFunction);
  }

  remove() {
    this.interrupt = true;
    this.hide();
    this._entityKeys.forEach(key => this[key] && this[key].remove());
  }

  isAnimating() {
    return !this.line.animationComplete();
  }

  prepareAnimation() {
    this.endMarker.hide();
    this.midwayMarker.hide();
    this.animationMarker.hide();
    this.line.animationReset();
    this.startMarker.show();
    this.outline && this.outline.show();
  }

  animate({ time = null, onDone = null }) {
    if (time && time < 1000) {
      throw `Invalid animation options: time (${time}) cannot be less than 1000`;
    }
    const animationTime = time || this.line.baseTime;
    const refreshRate = (animationTime / this.line.baseTime) * this.line.baseRefreshRate;

    this.prepareAnimation();
    let excessDistance = 0;
    const intervalId = setInterval(() => {
      if (this.interrupt) {
        this.animationMarker.hide();
        return;
      }
      const result = this.line.nextAnimationTick({ previousExcessDistance: excessDistance });
      const currentTerminus = result.terminus;
      excessDistance = result.excessDistance;

      if (currentTerminus) {
        this.animationMarker.point = currentTerminus;
        this.animationMarker.show();
      } else {
        clearInterval(intervalId);
        if (!this.interrupt) {
          this.animationMarker.point = null;
          this.showStartAndEndMarkers();
          this.midwayMarker.map.on('zoomend', this.zoomEndFunction);
        }
        if (onDone) onDone();
      }
    }, refreshRate);
  }

  showStartAndEndMarkers() {
    if (this.areStartAndEndMarkersConflated() && this.line.animationComplete()) {
      this.startMarker.hide();
      this.endMarker.hide();
      this.midwayMarker.show();
    } else {
      this.startMarker.show();
      this.endMarker.show();
      this.midwayMarker.hide();
    }
  }
}

export { buildRouteLayerEntities, RouteLayerManager };
